import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, onMounted, ref } from 'vue';
import { store } from '@/store';
import VueProgrammaticInvisibleGoogleRecaptcha from 'vue-programmatic-invisible-google-recaptcha';
import userAgent from '@/services/userAgent.service';
import dictionaryService from '@/core/dictionary.service';
import ScrollService from '@/core/scroll.service';
import { useRouter } from 'vue-router/composables';
import { useValidator } from '@/core/app.service';
import { useTranslations } from '@/core/translate.mixin';
import viewportEventsService from '@/core/responsive/viewport/viewport-events.service';
import BlockTraderSignupProgressBar from './BlockTraderSignupProgressBar.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BlockTraderSignupForm',
  props: {
    model: null
  },
  setup(__props: any) {

const props = __props as {model: TraderSignupFormBlockJsonViewModel}



const recaptchaKey = computed<string>(() => store.getters['app/recaptchaKey']);

function submitSignupForm(request: TraderSignupFormBlockWebRequest): Promise<string> {
    return store.dispatch('traderform/submitSignupForm', request);
}

const step = ref<number>(1);

const submittingFormData = ref<boolean>(false);
const request = ref<TraderSignupFormBlockWebRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    companyType: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    website: '',
    licenseOrOrganization: '',
    numberOfEmployees: '',
    numberOfProjectsAnnually: '',
    projectType: '',
    recaptchaToken: '',
    interestedInAudoTradeProgram: ''
});

const _uid = Math.floor(Math.random() * 1000000).toString();
const recaptchaComponentId = 'traderSignupFormRecaptcha';
const submitSuccess = ref(null);
const upload = ref<Vue>(null);

const refs = { [recaptchaComponentId + _uid]: ref(null) };

function setRecaptchaRef(el: any) {
    setTimeout(() => {
        refs[recaptchaComponentId + _uid].value = el;
    }, 100);
}
const runAnimations = ref<boolean>(userAgent.isPrerender);

const filename = ref<string>('');
const formSubmitted = ref<boolean>(false);

const $validator = useValidator();
$validator.extend(
    'filesize', {
        getMessage: _ => dictionaryService.get('Pages.TraderSignup.FormLabels.FileSizeErrorMessage'),
        validate: _ => {
            const file = (upload.value.$refs.input as HTMLInputElement).files[0];
            return file.size < 10 * 1024 * 1024;
        }
    });

const { t } = useTranslations();

const signUpLinkHeadline = t('Pages.TraderSignup.FormLabels.SignUpLinkHeadline').replace('{0}', props.model.loginPageUrl);

const progressText = computed<string>(() => {
    const base = t('Pages.TraderSignup.FormLabels.ProgressText');
    return base.replace('{0}', step.value.toString());
});

onMounted(() => {
    request.value.country = props.model.country;
});

function initAnimations(): void {
    runAnimations.value = true;
}

function validateAndSubmitRequest() {
    $validator
        .validateAll()
        .then(isValid => {
            if (isValid) {
                const recaptchaComponent = refs[recaptchaComponentId + _uid].value as any;
                recaptchaComponent.execute();
            } else {
                scrollInvalidElementIntoView();
            }
        })
        .catch(x => console.log('catch' + x));
}

const file = ref(null);

const router = useRouter();

function submitForm(recaptchaToken: string) {
    submittingFormData.value = true;
    request.value.recaptchaToken = recaptchaToken;

    const formData: any = new FormData();

    for (const key of Object.keys(request.value)) {
        formData.append(key, request.value[key]);
    };

    const fileInputField: HTMLInputElement = document.querySelector('#traderSignupForm input[type=\'file\']');
    for (let i = 0; i < fileInputField.files.length; i++) {
        formData.append('upload[]', fileInputField.files[i], fileInputField.files[i].name);
    }

    submitSignupForm(formData as TraderSignupFormBlockWebRequest).then(
        () => {
            router.replace({ query: {} });
            handleSuccess();
        },
        () => {
            submittingFormData.value = false;
        }
    );
}

const step1Fields = ['firstname', 'lastname', 'email', 'phone'];
const step1SubmitError = ref<boolean>(false);
function goToStep2() {
    $validator.validateAll(step1Fields)
        .then(isValid => {
            step1SubmitError.value = !isValid;
            if (isValid) {
                step.value++;
                scrollToTop();
            } else {
                scrollInvalidElementIntoView();
            }
        });
}

const step2Fields = ['company', 'companyType', 'address', 'address2', 'city', 'state', 'zipCode', 'country', 'website'];
const step2SubmitError = ref<boolean>(false);
function goToStep3() {
    $validator.validateAll(step2Fields)
        .then(isValid => {
            step2SubmitError.value = !isValid;
            if (isValid) {
                step.value++;
                scrollToTop();
            } else {
                scrollInvalidElementIntoView();
            }
        });
}

function goBack() {
    step.value--;
    scrollToTop();
}

function enterClicked() {
    if (step.value === 1) {
        goToStep2();
    } else if (step.value === 2) {
        goToStep3();
    } else if (step.value === 3) {
        validateAndSubmitRequest();
    }
}

function clickUploadField() {
    (upload.value.$refs.input as HTMLInputElement).click();
}

function deleteFile() {
    (upload.value.$refs.input as HTMLInputElement).files = new DataTransfer().files;
    file.value = null;
    filename.value = null;
}

function onFileSelected() {
    filename.value =
            upload.value && (upload.value.$refs.input as HTMLInputElement).files.length
                ? (upload.value.$refs.input as HTMLInputElement).files[0].name
                : '';
}

function scrollToTop() {
    scrollToElement('step-top');
}

function scrollToElement(id: string) {
    const elementToScrollTo = document.getElementById(id);
    ScrollService.scrollToElement(elementToScrollTo, viewportEventsService.viewport.scrollY, 300);
}

function scrollInvalidElementIntoView() {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
}

function handleSuccess() {
    formSubmitted.value = true;
    submittingFormData.value = false;
    setTimeout(() => {
        submitSuccess.value.scrollIntoView({ behavior: 'smooth' });
    }, 100);
}


return { __sfc: true,props, recaptchaKey, submitSignupForm, step, submittingFormData, request, _uid, recaptchaComponentId, submitSuccess, upload, refs, setRecaptchaRef, runAnimations, filename, formSubmitted, $validator, t, signUpLinkHeadline, progressText, initAnimations, validateAndSubmitRequest, file, router, submitForm, step1Fields, step1SubmitError, goToStep2, step2Fields, step2SubmitError, goToStep3, goBack, enterClicked, clickUploadField, deleteFile, onFileSelected, scrollToTop, scrollToElement, scrollInvalidElementIntoView, handleSuccess, VueProgrammaticInvisibleGoogleRecaptcha, BlockTraderSignupProgressBar }
}

})